<template>
  <span class="audit-log-viewer">
    <button 
      type="button" 
      class="btn btn-primary btn-sm" 
      @click="toggleLog"
    >
      {{ isVisible ? 'Hide Log' : 'View Log' }}
    </button>
    
    <div v-if="isVisible" class="log-container mt-2">
      <div v-if="loading" class="text-center p-3">
        Loading...
      </div>
      
      <div v-else-if="jobLogs.length === 0" class="log-empty">
        No jobLogs found
      </div>

      
      <div v-else class="log-list">
        <div 
          v-for="log in jobLogs" 
          :key="log.id"
          class="log-item"
        >
          <div class="log-header">
            <strong> {{ log.action }}</strong> <strong> At:</strong>{{formatDate(log.changedAt) }}
                <strong>By:</strong>{{log.userType}}:{{ log.changedByOperatorName + "(" + log.changedByOperatorId + ")" }}
          </div>
          
          <div class="log-changes text-left">
            <div 
              v-for="(change, field) in log.parsedChanges" 
              :key="field"
              class="change-item"
            >

              <span v-if="field === 'StatusId'">
                      JobStatus: {{ statusMap[change.Old] || change.Old }} → {{ statusMap[change.New] || change.New }}
              </span>

              <span v-else-if="field === 'RecordStatusId'">
                RecordStatus: {{ recordStatusMap[change.Old] || change.Old }} → {{ recordStatusMap[change.New] || change.New }}
              </span>

              <span v-else-if="field !== 'RecordDateTime'">
                <span class="field-name">{{ formatFieldName(field) }}:</span>
                <span class="old-value">{{ formatValue(field, change.Old) }}</span>
                →
                <span class="new-value">{{ formatValue(field, change.New) }}</span>
              </span>

            </div>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue';  // Add onMounted import

// Props - keep as is
const props = defineProps({
    entityType: { type: String, required: true },
    entityId: { type: [Number, String], required: true },
    statusMappings: { type: Object, default: () => ({}) },
    fieldMappings: { type: Object, default: () => ({}) },
    officers: { type: Array, default: () => [] },
    sites: { type: Array, default: () => [] },
    jobStatuses: { type: Array, default: () => [] },
    isLogVisible: { type: Boolean, default: true },
    baseUrl: { type: String, default: '' },  
});

// State
const isVisible = ref(false);
const loading = ref(false);
const jobLogs = ref([]);

// Constants
const baseUrl = props.baseUrl || '';

// Computed (keep as is)
const defaultStatusMap = computed(() => ({
  1: 'Schedule',
  2: 'In Progress',
  3: 'Completed',
  4: 'Cancelled',
  ...props.statusMappings
}));

const statusMap = computed(() => ({
        1: 'Schedule',
        2: 'OK',
        3: 'TBC',
        4: 'Late',
        5: 'Left Earlier',
        6: 'Chased Up',
        7: 'Accepted',
        8: 'Refused',
        9: 'Ended',
      }));

const recordStatusMap = computed(() => ({
          null: 'New',
          1: 'New',
          2: 'Updated',
          3: 'Cancelled',
      })); 

const defaultFieldMap = computed(() => ({
  'StatusId': 'Status',
  'OfficerId': 'Security Officer',
  'ShiftStart': 'Shift Start',
  'ShiftEnd': 'Shift End',
  ...props.fieldMappings
}));

// Methods
const toggleLog = () => {
  isVisible.value = !isVisible.value;
  if (isVisible.value && jobLogs.value.length === 0) {  // Fix: jobLogs.value not logs.value
    fetchLogs(props.entityType, props.entityId);
  }
};

const fetchLogs = async (entityType, entityId) => {
  loading.value = true;
  
  const url = `${baseUrl}Compliance`;
  const entityInfo = {
    entityType: entityType,
    entityId: entityId
  };
  
  //console.log("Fetching logs for:", entityInfo);  // Debug log
  
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(entityInfo)
    });
    
    const data = await response.json();
    //console.log("Response data:", data);  // Debug log
    
    jobLogs.value = (Array.isArray(data) ? data : (data.result || [])).map((log) => ({
      ...log,
      parsedChanges: log.changeSummary ? parseChanges(log.changeSummary) : null
    }));
    
    //console.log("Processed logs:", jobLogs.value);  // Debug log
    
  } catch (error) {
    console.error("Error fetching audit jobLogs:", error);
    jobLogs.value = [];
  } finally {
    loading.value = false;
  }
};

const parseChanges = (changeSummary) => {
  try {
    return JSON.parse(changeSummary);
  } catch {
    return null;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString + 'Z');
  return date.toLocaleString();
};

const formatFieldName = (field) => {
  return defaultFieldMap.value[field] || field;
};

const formatValue = (field, value) => {
  if (field === 'StatusId' && value) {
    return defaultStatusMap.value[value] || value;
  }
  
  if (value === null || value === undefined) {
    return '(empty)';
  }
  
  return value;
};

// Add this watch to react to entityId changes
watch(() => props.entityId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    //console.log("entityId changed from", oldId, "to", newId);
    fetchLogs(props.entityType, newId);
  }
});

// LIFECYCLE
onMounted(() => {
  //console.log("Props entityType:", props.entityType);  // Debug log
  //console.log("Props entityId:", props.entityId);      // Debug log
  fetchLogs(props.entityType, props.entityId);
});
</script>

<style scoped>
.audit-log-viewer {
  margin: 10px 0;
}

.log-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}

.log-list {
  max-height: 400px;
  overflow-y: auto;
}

.log-item {
  border-bottom: 1px solid #eee;
  padding: 12px;
}

.log-item:last-child {
  border-bottom: none;
}

.log-header {
  font-size: 12px;
  color: #666;
  margin-bottom: 0px;
  padding-bottom: 0px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #f0f0f0;
}

.log-header strong {
  color: #333;
}

.reason {
  color: #0066cc;
  margin-left: 8px;
}

.log-changes {
  font-size: 13px;
  font-family: monospace;
}

.change-item {
  padding: 4px 0;
}

.field-name {
  font-weight: bold;
  color: #333;
  min-width: 120px;
  display: inline-block;
}

.old-value {
  color: #dc3545;
  text-decoration: line-through;
  margin: 0 8px;
}

.new-value {
  color: #28a745;
  font-weight: 500;
  margin-left: 8px;
}

.log-empty {
  padding: 20px;
  text-align: center;
  color: #999;
  font-style: italic;
}
</style>